import React from 'react';
import {
    Divider,
    useAppDisplayModeContext,
    useStyles,
    useTheme,
} from '@archipro-design/aria';
import * as S from './StockAndShipping.style';
import { useVariantStock } from '../../hook/use-variant-stock';
import ClickAndCollect from './ClickAndCollect';
import { useTracker } from '@archipro-website/tracker';
import { useVariantSelectionState } from '../../context/VariantSelectionContext';
import TradeOnlyTag from './TradeOnlyTag';
import Delivery from '@modules/product/component/variant-purchase/Delivery';
import ShippingPopup from '@modules/ecommerce/component/shipping-popup/ShippingPopup';
import { useVariantShipping } from '~/modules/product/hook/use-variant-shipping';
import RefundPolicy from '@modules/product/component/refund-policy/RefundPolicy';
import type { RefundPolicyData } from '@modules/product/component/refund-policy/RefundPolicy';

interface StockAndShippingProps {
    /** Data required to render the refund policy link. */
    refundPolicyData?: RefundPolicyData;
}

const StockAndShipping = ({ refundPolicyData }: StockAndShippingProps) => {
    const { hasClickAndCollect, hasShippingType } = useVariantShipping();
    const { stockDescription, hasLeadTime, canAddToCart, showAsInStock } =
        useVariantStock({ hasClickAndCollect, hasShippingType });
    const { selectedVariant } = useVariantSelectionState();
    const [modalOpen, setModalOpen] = React.useState(false);
    const { mobile } = useAppDisplayModeContext();
    const theme = useTheme();
    const tracker = useTracker();

    const showTradeOnly =
        !canAddToCart &&
        selectedVariant?.TradeOnlyEnabled &&
        selectedVariant?.TradePrice;

    const { css } = useStyles({
        showTradeOnly: Boolean(showTradeOnly),
    });

    return (
        <div className={css(S.StockAndShipping)}>
            <div className={css(S.Stock)}>{stockDescription}</div>
            {showAsInStock && (
                <>
                    {showTradeOnly && (
                        <div className={css(S.TradeOnlyContainer)}>
                            <TradeOnlyTag />
                        </div>
                    )}
                    <Delivery
                        isSample={false}
                        onRegionClick={() => {
                            tracker.log('ShippingLocationModalOpen', {
                                url: new URL(window.location.href),
                                targetTracker: 'archiproTracker',
                            });
                            if (canAddToCart) {
                                setModalOpen(true);
                            }
                        }}
                        hasLeadTime={hasLeadTime}
                    />
                    <ClickAndCollect
                        isSample={false}
                        onLocationClick={() =>
                            canAddToCart && setModalOpen(true)
                        }
                        hasLeadTime={hasLeadTime}
                    />
                    {refundPolicyData && (
                        <RefundPolicy data={refundPolicyData} />
                    )}
                </>
            )}
            <ShippingPopup
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onConfirm={() => setModalOpen(false)}
                isSample={false}
            />
            {mobile && (
                <Divider
                    className={css(S.Divider)}
                    size={1}
                    variables={{
                        dividerPrimaryColor:
                            theme.siteVariables.colors.grey[125],
                    }}
                />
            )}
        </div>
    );
};

export default StockAndShipping;
